/* eslint-disable max-statements */

import { StyleSheet } from "@react-pdf/renderer";
import { fetchCommentsPdfReport } from "data/services/api/comment/commentDetails";
import { PDFDocument } from "pdf-lib";
import React from "react";
import { CommentsPdf } from "ui/partials/comment/CommentsPdf";
import { mapEntities2TitleArray } from "utils/entityTable";
import { baseA4Styles, generatePdfBlob } from "./pdfGenerator";

const commentStyles = StyleSheet.create({
  ...baseA4Styles,
  entitiesSection: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1 solid #e0e0e0",
  },
  entityItem: {
    fontSize: 9,
    color: "#555",
    marginBottom: 4,
    marginLeft: 10,
  },
  tagsSection: {
    backgroundColor: "#fafafa",
    padding: 12,
    marginBottom: 15,
    borderRadius: 6,
    border: "1 solid #e0e0e0",
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  tag: {
    fontSize: 9,
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    padding: "4 8",
    marginRight: 6,
    marginBottom: 6,
    borderRadius: 4,
    border: "1 solid #bbdefb",
  },
  aiSummarySection: {
    backgroundColor: "#e3f2fd",
    padding: 15,
    marginBottom: 15,
    borderRadius: 6,
    border: "1 solid #90caf9",
  },
  aiSummaryTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#1565c0",
    marginBottom: 10,
  },
  aiSummaryText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: "#424242",
  },
  restrictedContainer: {
    border: "2 solid #d32f2f",
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
  },
  clientReadyContainer: {
    border: "2 solid #1976d2",
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
  },
  subsectionTitle: {
    fontWeight: "bold",
    fontSize: 10,
    color: "#616161",
    marginTop: 8,
    marginBottom: 6,
  },
  fundCommentsSection: {
    marginBottom: 15,
  },
  fundCommentBox: {
    backgroundColor: "#f5f5f5",
    padding: 12,
    marginBottom: 12,
    borderRadius: 6,
    border: "1 solid #e0e0e0",
  },
  fundCommentTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#1976d2",
    marginBottom: 12,
    marginTop: 8,
    letterSpacing: 0.8,
  },
  fundCommentSectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#424242",
    marginTop: 12,
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  fundCommentContent: {
    marginBottom: 12,
  },
  tableOfContentsPage: {
    padding: 40,
    fontSize: 11,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  tableOfContentsTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1976d2",
    marginBottom: 30,
    textAlign: "center",
  },
  tableOfContentsItem: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f5f5f5",
    borderRadius: 6,
    border: "1 solid #e0e0e0",
  },
  tableOfContentsItemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  tableOfContentsLink: {
    color: "#1976d2",
    textDecoration: "none",
    fontSize: 12,
    fontWeight: "bold",
    flex: 1,
    flexWrap: "wrap",
  },
  tableOfContentsPageNumber: {
    fontSize: 9,
    color: "#ffffff",
    backgroundColor: "#1976d2",
    fontWeight: "bold",
    padding: "4 8",
    borderRadius: 12,
    minWidth: 30,
    textAlign: "center",
  },
  tableOfContentsMetadata: {
    fontSize: 9,
    color: "#616161",
    marginTop: 4,
  },
  pageFooterWithPagination: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
    color: "#616161",
  },
});

const CHUNK_SIZE = 50;

/**
 * Generate and download a PDF with multiple comments.
 * Renders in chunks to avoid browser OOM with large exports.
 */
export async function downloadCommentsPdf(
  commentIds: number[],
  onProgress?: (message: string) => void,
): Promise<void> {
  if (commentIds.length === 0) {
    onProgress?.("No comments to export");
    return;
  }

  try {
    onProgress?.("Fetching data...");

    const commentsData = await getCommentsData(commentIds);

    const tableOfContentsData = commentsData.map((data, idx) => {
      const comment = data.commentDetails;
      const entities = data?.commentDetails?.entities;

      let titleText = comment.customTitle;
      if (!titleText && entities) {
        const entityTitles = mapEntities2TitleArray(entities);
        titleText = entityTitles.join(", ");
      }

      return {
        id: `comment-${idx}`,
        title: titleText || `Comment ${comment.id}`,
        type: comment.commentType,
        author: comment.author?.name,
        date: comment.commentDate,
        index: idx + 1,
      };
    });

    const chunks: (typeof commentsData)[] = [];
    for (let i = 0; i < commentsData.length; i += CHUNK_SIZE) {
      chunks.push(commentsData.slice(i, i + CHUNK_SIZE));
    }

    const totalChunks = chunks.length;
    const blobs: Blob[] = [];

    for (let i = 0; i < totalChunks; i++) {
      const percent = Math.round(((i + 1) / totalChunks) * 100);
      onProgress?.(`Generating PDF ${percent}%`);

      const chunkDocument = (
        <CommentsPdf
          tableOfContentsData={i === 0 ? tableOfContentsData : []}
          commentsData={chunks[i]}
          commentCount={commentIds.length}
          styles={commentStyles}
        />
      );

      const blob = await generatePdfBlob(chunkDocument);
      blobs.push(blob);
    }

    onProgress?.("Merging PDF...");

    const mergedPdf = await PDFDocument.create();
    for (const blob of blobs) {
      const pdfBytes = await blob.arrayBuffer();
      const chunk = await PDFDocument.load(pdfBytes);
      const pages = await mergedPdf.copyPages(chunk, chunk.getPageIndices());
      for (const page of pages) {
        mergedPdf.addPage(page);
      }
    }

    const finalBytes = await mergedPdf.save();
    const finalBlob = new Blob([finalBytes as BlobPart], {
      type: "application/pdf",
    });

    onProgress?.("Downloading PDF...");

    const url = URL.createObjectURL(finalBlob);
    const link = globalThis.document.createElement("a");
    link.href = url;
    link.download = `comments-${commentIds.length}-items.pdf`;
    globalThis.document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 100);

    onProgress?.("PDF downloaded!");
  } catch (error) {
    console.error("Error generating comment PDF:", error);
    throw error;
  }
}

async function getCommentsData(commentIds: number[]) {
  const comments = await fetchCommentsPdfReport(commentIds);

  if (comments.length === 0 && commentIds.length > 0) {
    throw new Error("Failed to fetch comments");
  }

  return comments.map((comment) => ({
    commentDetails: comment,
    tags: comment.tags ?? [],
  }));
}
